import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { hanMucTokenRepository } from '../../../lib/repositories/han-muc-token.repository';
import { useToast } from '../../../lib/toast';
import type { AiTokenPriority, AiTokenQuota } from '../types';
import { KHOA_TENANT } from './useTenants';

/**
 * Khoá cache của hạn mức token AI (ADR-0088). Mọi khoá bắt đầu bằng `['han-muc-token']`, nên đổi hạn
 * mức theo gói vô hiệu hoá mọi tenant bằng một khoá mà không phải biết tenant nào thuộc gói nào.
 */
export const KHOA_TOKEN_AI = {
  tatCa: ['han-muc-token'] as const,
  cuaTenant: (id: string) => ['han-muc-token', 'tenant', id] as const,
  theoGoi: ['han-muc-token', 'goi'] as const,
};

/** 4xx là câu trả lời của server (không có quyền, không có tenant): thử lại chỉ làm chậm. */
export function thuLaiToken(soLanDaThu: number, err: unknown): boolean {
  const status = (err as ApiError | undefined)?.status;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return soLanDaThu < 1;
}

/**
 * Hạn mức token AI tháng này của một tenant. `tenantId` trống thì không gọi — ở cổng tenant, chờ ngữ
 * cảnh đăng nhập trả `tenant_id` (`useTenantCuaToi`).
 *
 * Số đã dùng tăng theo từng lời gọi AI của module, nên đọc lại khi quay về tab và mỗi phút một lần
 * trong lúc màn đang mở; không cần đẩy sự kiện cho một con số đếm.
 */
export function useHanMucTokenAi(tenantId: string | undefined) {
  return useQuery<AiTokenQuota>({
    queryKey: KHOA_TOKEN_AI.cuaTenant(tenantId ?? ''),
    queryFn: () => hanMucTokenRepository.cuaTenant(tenantId ?? ''),
    enabled: Boolean(tenantId),
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: thuLaiToken,
  });
}

/**
 * Đặt (`so`) hoặc bỏ (`null`) hạn mức riêng của một tenant. Ghi kết quả vào cache ngay, rồi tải lại
 * chi tiết tenant vì dòng `AI_TOKENS_MONTH` của bảng hạn mức đổi theo.
 *
 * Lỗi **không** toast: hộp thoại giữ số vừa nhập và hiện lỗi ngay dưới ô.
 */
export function useDatHanMucTokenAi(tenantId: string, tenTenant: string) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useT();

  return useMutation({
    mutationFn: (limitOverride: number | null) =>
      hanMucTokenRepository.datGhiDe(tenantId, { limit_override: limitOverride }),
    onSuccess: (moi, limitOverride) => {
      queryClient.setQueryData(KHOA_TOKEN_AI.cuaTenant(tenantId), moi);
      void queryClient.invalidateQueries({ queryKey: KHOA_TENANT.chiTiet(tenantId) });
      toast.success(
        limitOverride === null ? t('hanMucToken.daBo', { ten: tenTenant }) : t('hanMucToken.daDat', { ten: tenTenant }),
      );
    },
  });
}

/**
 * Đặt thứ tự trừ token (ADR-0091 mục 4). Ghi hạn mức trả về vào cache ngay: thứ tự túi trên thẻ đổi theo. Lỗi
 * không toast: khối chọn hiện lỗi tại chỗ.
 */
export function useDatUuTienToken(tenantId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useT();

  return useMutation({
    mutationFn: (priority: AiTokenPriority) => hanMucTokenRepository.datUuTien(tenantId, priority),
    onSuccess: (moi) => {
      queryClient.setQueryData(KHOA_TOKEN_AI.cuaTenant(tenantId), moi);
      toast.success(t('tokenAi.daDatUuTien'));
    },
  });
}
